"use client";

import { Building2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Pagination } from "@/components/frontend/ui/pagination";
import { cn } from "@/lib/utils";
import PartnerCard from "./card";
import { usePartnerOverview } from "./context";
import { sortOptionValues } from "./constants";
import Combobox, { type ComboboxOption } from "@/components/frontend/ui/form/inputs/combobox";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";

const columnMap = {
	"2": "grid-cols-1 sm:grid-cols-2",
	"3": "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
	"4": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};

export function PartnerOverviewResults() {
	const { data, pending, settings, onPageChange, sort, onSortChange } = usePartnerOverview();
	const t = useTranslations("ms.partner-overview");
	const sortOptions: ComboboxOption[] = [
		{ label: t("sort-placeholder"), value: "" },
		...sortOptionValues.map((value) => ({ label: t(`sort-options.${value}`), value })),
	];

	if (!data?.data.length) {
		return (
			<div className="flex w-full flex-col items-center justify-start gap-2 py-16">
				<Building2 className="size-12" />

				<div className="text-center">
					<p className="h3">{t("no-results-title")}</p>
					<p className="text-sm">{t("no-results-description")}</p>
				</div>
			</div>
		);
	}

	return (
		<div className={cn("w-full space-y-4", pending && "opacity-60 transition-opacity")}>
			<div className="flex items-center justify-between gap-4">
				<p className="text-sm sm:text-lg">{t("results-summary", { total: data.meta.total })}</p>

				<div className={cn("ms-form-" + resolveRelationshipId(settings?.sortFormVariant), "flex items-center gap-2")}>
					<span className="hidden sm:block">{t("sort-by")}</span>
					<Combobox
						containerClassName="min-w-60"
						clearable={false}
						search={false}
						formVariant={settings?.sortFormVariant ?? undefined}
						options={sortOptions}
						value={sortOptions.find((o) => o.value === sort) ?? sortOptions[0]}
						onValueChange={(option) => {
							if (!option) return;
							onSortChange(option.value);
						}}
					/>
				</div>
			</div>

			<ol className={cn("grid gap-x-4 gap-y-6", columnMap[settings?.columns ?? "3"])}>
				{data.data.map((partner) => (
					<li key={partner.id}>
						<PartnerCard partner={partner} variant={settings?.partnerCard} childVariant={settings?.partnerChildCard} />
					</li>
				))}
			</ol>

			{data.meta.last_page > 1 && <Pagination data={data} variant={settings?.paginationButton} onPageChange={onPageChange} />}
		</div>
	);
}
